import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { flushSync } from "react-dom";
import { Link, useParams, useSearchParams } from "react-router";
import { Mars, Pin, Settings, Venus } from "lucide-react";
import { toast } from "sonner";
import { ContactChurnedBadge } from "@/components/journey/ContactChurnedBadge";
import { useCustomerChat } from "@/components/journey/CustomerChatProvider";
import { NewContactDefaultsPanel } from "@/components/journey/NewContactDefaultsPanel";
import { WecomTagMultiSelect } from "@/components/journey/WecomTagMultiSelect";
import { WecomUserMultiSelect } from "@/components/journey/WecomUserMultiSelect";
import {
  AdminEmptyState,
  AdminList,
  AdminListRow,
  AdminPageHeader,
  AdminPill,
} from "@/components/shell/AdminPage";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAccountWorkspace } from "@/lib/account-workspace";
import {
  listCustomerBindings,
} from "@/lib/journey-bindings";
import {
  JourneyApiError,
  listJourneys,
  type JourneyDefinition,
} from "@/lib/journey-api";
import {
  ApiError,
  contactTypeLabel,
  formatLastInboundHint,
  formatWecomTagLabel,
  listWecomExternalContacts,
  mergeAndSortPinnedContact,
  restoreElementScrollTop,
  updateWecomExternalContact,
  workspaceMainScrollElement,
  type WecomExternalContact,
  type WecomExternalContactsSort,
} from "@/lib/wecom-external-contacts";
import {
  formatFollowUserids,
  wecomUserDisplayName,
  type WecomUser,
} from "@/lib/wecom-users";

/** 筛选项「全部」用哨兵，避免 Select 空字符串 */
const FILTER_ALL = "all";
/** 旅程筛选：未绑定 */
const FILTER_JOURNEY_NONE = "none";
/** 旅程筛选开启时，先拉齐联系人再本地过滤的每页大小 */
const JOURNEY_FILTER_FETCH_PER_PAGE = 50;
/** 安全上限：最多翻页数，避免超大账户一次拖垮浏览器 */
const JOURNEY_FILTER_MAX_PAGES = 20;
const LIST_PER_PAGE = 20;

type GenderFilter = typeof FILTER_ALL | "0" | "1" | "2";
/** 全部 | 未绑定 | 具体旅程 id */
type JourneyFilter = typeof FILTER_ALL | typeof FILTER_JOURNEY_NONE | string;

const SORT_OPTIONS: { value: WecomExternalContactsSort; label: string }[] = [
  { value: "last_activity", label: "最近消息" },
  { value: "created_at", label: "最近同步" },
  { value: "name", label: "昵称 A→Z" },
];

function parseGenderFilter(value: GenderFilter): 0 | 1 | 2 | undefined {
  if (value === "0") return 0;
  if (value === "1") return 1;
  if (value === "2") return 2;
  return undefined;
}

/** 拉取品牌下活跃绑定的 external_userid 集合（可按旅程收窄） */
async function fetchBoundExternalUserids(
  accountId: string | number,
  brandId: string | number,
  journeyDefinitionId?: string | number,
): Promise<Set<string>> {
  const payload = await listCustomerBindings(accountId, {
    brandId,
    journeyDefinitionId,
  });
  return new Set(
    (payload.customer_bindings ?? []).map((b) => b.external_userid),
  );
}

/** 在其它筛选条件下翻页拉齐联系人（有上限），供旅程本地过滤 */
async function fetchAllContactsForJourneyFilter(
  accountId: string | number,
  opts: {
    q: string;
    followUserids: string[];
    wecomTagIds: string[];
    gender?: 0 | 1 | 2;
    sortBy: WecomExternalContactsSort;
  },
): Promise<WecomExternalContact[]> {
  const all: WecomExternalContact[] = [];
  let page = 1;
  let totalPages = 1;
  while (page <= totalPages && page <= JOURNEY_FILTER_MAX_PAGES) {
    const payload = await listWecomExternalContacts(accountId, {
      page,
      perPage: JOURNEY_FILTER_FETCH_PER_PAGE,
      q: opts.q || undefined,
      followUserids: opts.followUserids.length > 0 ? opts.followUserids : undefined,
      wecomTagIds: opts.wecomTagIds.length > 0 ? opts.wecomTagIds : undefined,
      gender: opts.gender,
      sort: opts.sortBy,
    });
    all.push(...(payload.external_contacts ?? []));
    totalPages = Math.max(1, payload.pagination?.total_pages ?? 1);
    page += 1;
  }
  return all;
}

/** 客户：读账户级企微外部联系人缓存（与企微接入页同源） */
export default function JourneyCustomersPage() {
  const { brandId } = useParams();
  const [searchParams] = useSearchParams();
  const { accountId } = useAccountWorkspace();
  const { openCustomerChat } = useCustomerChat();
  const [contacts, setContacts] = useState<WecomExternalContact[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const [selectedFollowUserids, setSelectedFollowUserids] = useState<string[]>([]);
  const [selectedWecomTagIds, setSelectedWecomTagIds] = useState<string[]>([]);
  const [genderFilter, setGenderFilter] = useState<GenderFilter>(FILTER_ALL);
  /** 按旅程筛选：全部 | 未绑定 | 具体旅程 id；总览可经 ?journey= 深链 */
  const [journeyFilter, setJourneyFilter] = useState<JourneyFilter>(() => {
    const fromUrl = searchParams.get("journey")?.trim();
    if (!fromUrl) return FILTER_ALL;
    if (fromUrl === FILTER_JOURNEY_NONE) return FILTER_JOURNEY_NONE;
    return fromUrl;
  });
  const [filterJourneys, setFilterJourneys] = useState<JourneyDefinition[]>([]);
  /** 默认：最后收发（入站）在前 */
  const [sort, setSort] = useState<WecomExternalContactsSort>("last_activity");
  const [knownUsersById, setKnownUsersById] = useState<Record<string, WecomUser>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pinSavingId, setPinSavingId] = useState<number | null>(null);
  /** 新客户默认设置弹层 */
  const [settingsOpen, setSettingsOpen] = useState(false);
  const nameByUserid = useMemo(() => {
    const map: Record<string, string> = {};
    for (const user of Object.values(knownUsersById)) {
      map[user.userid] = wecomUserDisplayName(user);
    }
    return map;
  }, [knownUsersById]);

  // 总览深链：URL ?journey= 变化时同步筛选
  useEffect(() => {
    const fromUrl = searchParams.get("journey")?.trim();
    if (!fromUrl) return;
    setJourneyFilter(fromUrl === FILTER_JOURNEY_NONE ? FILTER_JOURNEY_NONE : fromUrl);
  }, [searchParams]);

  const mergeKnownUsers = useCallback((users: WecomUser[]) => {
    setKnownUsersById((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const user of users) {
        const existing = next[user.userid];
        if (
          !existing ||
          existing.name !== user.name ||
          existing.avatar_url !== user.avatar_url
        ) {
          next[user.userid] = user;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, []);

  const genderParam = parseGenderFilter(genderFilter);
  const journeyFilterActive = journeyFilter !== FILTER_ALL;
  const hasExtraFilters = genderFilter !== FILTER_ALL || journeyFilterActive;
  const hasAnyFilter =
    selectedFollowUserids.length > 0 ||
    selectedWecomTagIds.length > 0 ||
    Boolean(appliedQuery) ||
    hasExtraFilters;

  const load = useCallback(
    async (
      nextPage: number,
      q: string,
      followUserids: string[],
      wecomTagIds: string[],
      gender?: 0 | 1 | 2,
      sortBy: WecomExternalContactsSort = "last_activity",
      journey: JourneyFilter = FILTER_ALL,
    ) => {
      setIsLoading(true);
      setError(null);
      try {
        // 无旅程筛选：沿用服务端分页
        if (journey === FILTER_ALL || !brandId) {
          const payload = await listWecomExternalContacts(accountId, {
            page: nextPage,
            perPage: LIST_PER_PAGE,
            q: q || undefined,
            followUserids: followUserids.length > 0 ? followUserids : undefined,
            wecomTagIds: wecomTagIds.length > 0 ? wecomTagIds : undefined,
            gender,
            sort: sortBy,
          });
          setContacts(payload.external_contacts ?? []);
          setPage(payload.pagination?.page ?? nextPage);
          setTotalPages(Math.max(1, payload.pagination?.total_pages ?? 1));
          setTotal(payload.pagination?.total ?? 0);
          return;
        }

        // 按绑定过滤：先拉 bindings，再在其它筛选结果上本地过滤并分页
        const boundIds =
          journey === FILTER_JOURNEY_NONE
            ? await fetchBoundExternalUserids(accountId, brandId)
            : await fetchBoundExternalUserids(accountId, brandId, journey);

        const all = await fetchAllContactsForJourneyFilter(accountId, {
          q,
          followUserids,
          wecomTagIds,
          gender,
          sortBy,
        });
        const filtered =
          journey === FILTER_JOURNEY_NONE
            ? all.filter((c) => !boundIds.has(c.external_userid))
            : all.filter((c) => boundIds.has(c.external_userid));

        const totalCount = filtered.length;
        const pages = Math.max(1, Math.ceil(totalCount / LIST_PER_PAGE) || 1);
        const safePage = Math.min(Math.max(1, nextPage), pages);
        const start = (safePage - 1) * LIST_PER_PAGE;
        setContacts(filtered.slice(start, start + LIST_PER_PAGE));
        setPage(safePage);
        setTotalPages(pages);
        setTotal(totalCount);
      } catch (caught) {
        const message =
          caught instanceof JourneyApiError || caught instanceof ApiError
            ? caught.message
            : "无法加载客户列表";
        setError(message);
        setContacts([]);
      } finally {
        setIsLoading(false);
      }
    },
    [accountId, brandId],
  );

  useEffect(() => {
    void load(
      1,
      appliedQuery,
      selectedFollowUserids,
      selectedWecomTagIds,
      genderParam,
      sort,
      journeyFilter,
    );
  }, [
    load,
    appliedQuery,
    selectedFollowUserids,
    selectedWecomTagIds,
    genderParam,
    sort,
    journeyFilter,
  ]);

  // 本品牌已启用旅程（筛选项）
  useEffect(() => {
    if (!accountId || !brandId) {
      setFilterJourneys([]);
      return;
    }
    let cancelled = false;
    void listJourneys(accountId, brandId)
      .then((payload) => {
        if (cancelled) return;
        setFilterJourneys(
          (payload.journeys ?? []).filter((j) => j.status === "enabled"),
        );
      })
      .catch((caught) => {
        if (cancelled) return;
        setFilterJourneys([]);
        if (!(caught instanceof JourneyApiError)) {
          toast.error("无法加载旅程列表");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, brandId]);

  function submitSearch(event: FormEvent) {
    event.preventDefault();
    setAppliedQuery(query.trim());
  }

  async function togglePin(contact: WecomExternalContact) {
    if (pinSavingId != null) return;
    setPinSavingId(contact.id);
    const nextPinned = !contact.pinned;
    // 主区滚动容器；重排前列出 scrollTop，避免置顶后跳动
    const scrollEl = workspaceMainScrollElement();
    const scrollTop = scrollEl?.scrollTop ?? 0;
    try {
      const updated = await updateWecomExternalContact(accountId, contact.id, {
        pinned: nextPinned,
      });
      // 本地合并 + 重排，不整页 load（load 会 isLoading 卸列表导致滚回顶）
      flushSync(() => {
        setContacts((prev) => mergeAndSortPinnedContact(prev, updated, sort));
      });
      restoreElementScrollTop(scrollEl, scrollTop);
      toast.success(nextPinned ? "已置顶（账户内共用）" : "已取消置顶");
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "无法更新置顶");
    } finally {
      setPinSavingId(null);
    }
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="客户"
        description="账户在企微同步的外部联系人（全品牌共用客户池）。"
      />

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>新客户设置</DialogTitle>
            <DialogDescription>
              同步进来的新客户默认绑定哪条旅程。按本品牌保存。
            </DialogDescription>
          </DialogHeader>
          {accountId && brandId ? (
            <NewContactDefaultsPanel
              accountId={accountId}
              brandId={brandId}
              active={settingsOpen}
              idPrefix="customers-new-contact"
            />
          ) : (
            <p className="text-sm text-muted-foreground">缺少账户或品牌信息，无法加载设置。</p>
          )}
        </DialogContent>
      </Dialog>

      <div className="flex flex-wrap items-start gap-x-3 gap-y-4">
        <div className="min-w-[12rem] flex-1 space-y-1.5 basis-64">
          <p className="flex h-7 items-center text-sm font-medium">搜索</p>
          <form className="flex items-center gap-2" onSubmit={submitSearch}>
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索昵称或 external_userid"
              className="min-w-0 flex-1"
              aria-label="搜索客户"
            />
            <Button type="submit" size="sm" variant="secondary" disabled={isLoading}>
              搜索
            </Button>
          </form>
        </div>

        <div className="w-[6.5rem] shrink-0 space-y-1.5">
          <p className="flex h-7 items-center text-sm font-medium">性别</p>
          <Select
            value={genderFilter}
            onValueChange={(v) => setGenderFilter((v as GenderFilter) ?? FILTER_ALL)}
            disabled={isLoading}
          >
            <SelectTrigger className="w-full" aria-label="按性别筛选">
              <SelectValue placeholder="全部" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={FILTER_ALL}>全部</SelectItem>
              <SelectItem value="1">男</SelectItem>
              <SelectItem value="2">女</SelectItem>
              <SelectItem value="0">未知</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="w-[9rem] shrink-0 space-y-1.5">
          <p className="flex h-7 items-center text-sm font-medium">排序</p>
          <Select
            value={sort}
            onValueChange={(v) =>
              setSort((v as WecomExternalContactsSort) ?? "last_activity")
            }
            disabled={isLoading}
          >
            <SelectTrigger className="w-full" aria-label="客户列表排序">
              <SelectValue placeholder="最近消息" />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="w-[14rem] max-w-full shrink-0 space-y-1.5">
          <div className="flex h-7 flex-wrap items-center gap-2">
            <p className="text-sm font-medium">跟进人</p>
            {selectedFollowUserids.length > 0 ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs"
                disabled={isLoading}
                onClick={() => setSelectedFollowUserids([])}
              >
                清除跟进人
              </Button>
            ) : null}
          </div>
          <WecomUserMultiSelect
            accountId={accountId}
            value={selectedFollowUserids}
            onChange={setSelectedFollowUserids}
            onUsersResolved={mergeKnownUsers}
            disabled={isLoading}
          />
        </div>

        <div className="w-[14rem] max-w-full shrink-0 space-y-1.5">
          <div className="flex h-7 flex-wrap items-center gap-2">
            <p className="text-sm font-medium">企微标签</p>
            {selectedWecomTagIds.length > 0 ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs"
                disabled={isLoading}
                onClick={() => setSelectedWecomTagIds([])}
              >
                清除标签
              </Button>
            ) : null}
          </div>
          <WecomTagMultiSelect
            accountId={accountId}
            value={selectedWecomTagIds}
            onChange={setSelectedWecomTagIds}
            disabled={isLoading}
          />
        </div>

        <div className="w-[10rem] shrink-0 space-y-1.5">
          <p className="flex h-7 items-center text-sm font-medium">旅程</p>
          <Select
            value={journeyFilter}
            onValueChange={(v) => setJourneyFilter(v ?? FILTER_ALL)}
            disabled={isLoading}
          >
            <SelectTrigger className="w-full" aria-label="按旅程筛选">
              <SelectValue placeholder="全部" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={FILTER_ALL}>全部</SelectItem>
              <SelectItem value={FILTER_JOURNEY_NONE}>未绑定旅程</SelectItem>
              {filterJourneys.map((journey) => (
                <SelectItem key={journey.id} value={String(journey.id)}>
                  {journey.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {journeyFilterActive ? (
        <p className="text-xs text-muted-foreground" role="status">
          {journeyFilter === FILTER_JOURNEY_NONE
            ? "仅显示尚未绑定本品牌旅程的客户"
            : `仅显示已绑定「${filterJourneys.find((j) => String(j.id) === journeyFilter)?.name ?? "该旅程"}」的客户`}
        </p>
      ) : null}

      {isLoading ? <p className="text-sm text-muted-foreground">正在加载客户…</p> : null}

      {error ? (
        <p
          className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          role="alert"
        >
          {error}
        </p>
      ) : null}

      {!isLoading && !error && contacts.length === 0 ? (
        <AdminEmptyState>
          <p className="text-base font-medium text-foreground">
            {hasAnyFilter ? "没有匹配的客户" : "还没有外部客户"}
          </p>
          <p className="mt-1">
            {hasAnyFilter
              ? "当前筛选条件下没有匹配的客户，可调整搜索、性别、跟进人或旅程"
              : "请先在账户「企微接入」完成绑定并同步外部联系人"}
          </p>
          {!hasAnyFilter ? (
            <Button asChild className="mt-4" size="sm" variant="outline">
              <Link to={`/accounts/${accountId}/wecom`}>去企微接入同步</Link>
            </Button>
          ) : null}
        </AdminEmptyState>
      ) : null}

      {!isLoading && contacts.length > 0 ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">共 {total} 位客户</p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setSettingsOpen(true)}
            >
              <Settings data-icon="inline-start" />
              设置
            </Button>
          </div>
          <AdminList>
            {contacts.map((contact) => {
              const followLabel = formatFollowUserids(contact.follow_userids, nameByUserid, "、");
              const isPinned = Boolean(contact.pinned);
              return (
                <AdminListRow key={contact.id} className="p-0">
                  <div className="flex w-full min-w-0 items-stretch gap-0">
                    <button
                      type="button"
                      onClick={() => openCustomerChat(contact)}
                      className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-3 py-4 pl-4 pr-2 text-left transition-colors hover:bg-muted/40"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        {contact.avatar_url ? (
                          <img
                            src={contact.avatar_url}
                            alt=""
                            className="size-10 shrink-0 rounded-full object-cover"
                            referrerPolicy="no-referrer"
                          />
                        ) : (
                          <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-muted text-sm text-muted-foreground">
                            {(contact.name || "?").slice(0, 1)}
                          </span>
                        )}
                        <div className="min-w-0 space-y-1">
                          <p className="flex min-w-0 items-center gap-1.5 truncate font-medium">
                            {isPinned ? (
                              <Pin
                                className="size-3.5 shrink-0 text-primary"
                                aria-label="已置顶"
                              />
                            ) : null}
                            <span className="truncate">
                              {contact.name || "未获取昵称"}
                            </span>
                            {contact.gender === 1 ? (
                              <Mars
                                className="size-3.5 shrink-0 text-sky-500"
                                aria-label="男"
                              />
                            ) : contact.gender === 2 ? (
                              <Venus
                                className="size-3.5 shrink-0 text-pink-500"
                                aria-label="女"
                              />
                            ) : null}
                          </p>
                          {(contact.wecom_tags ?? []).length > 0 ? (
                            <div className="flex flex-wrap items-center gap-1">
                              {(contact.wecom_tags ?? []).map((tag) => {
                                const label = formatWecomTagLabel(tag);
                                if (!label) return null;
                                return (
                                  <AdminPill key={`${tag.id ?? tag.name}-${label}`} tone="muted">
                                    {label}
                                  </AdminPill>
                                );
                              })}
                            </div>
                          ) : null}
                          <p
                            className="truncate text-xs text-muted-foreground"
                            title={
                              contact.last_inbound_at
                                ? new Date(contact.last_inbound_at).toLocaleString("zh-CN")
                                : undefined
                            }
                          >
                            {formatLastInboundHint(contact.last_inbound_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <AdminPill tone="primary">{contactTypeLabel(contact.contact_type)}</AdminPill>
                        <ContactChurnedBadge churnedAt={contact.churned_at} />
                        {contact.follow_userids.length > 0 ? (
                          <span
                            className="max-w-40 truncate text-xs text-muted-foreground"
                            title={followLabel}
                          >
                            跟进 {followLabel}
                          </span>
                        ) : null}
                      </div>
                    </button>
                    <div className="flex shrink-0 items-center gap-2 pr-3">
                      <Button
                        type="button"
                        size="icon-sm"
                        variant="ghost"
                        disabled={pinSavingId === contact.id}
                        aria-label={isPinned ? "取消置顶" : "置顶"}
                        title={isPinned ? "取消置顶" : "置顶（账户共用）"}
                        onClick={() => void togglePin(contact)}
                      >
                        <Pin
                          className={
                            isPinned
                              ? "size-4 fill-primary text-primary"
                              : "size-4 text-muted-foreground"
                          }
                        />
                      </Button>
                    </div>
                  </div>
                </AdminListRow>
              );
            })}
          </AdminList>

          {totalPages > 1 ? (
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page <= 1 || isLoading}
                onClick={() =>
                  void load(
                    page - 1,
                    appliedQuery,
                    selectedFollowUserids,
                    selectedWecomTagIds,
                    genderParam,
                    sort,
                    journeyFilter,
                  )
                }
              >
                上一页
              </Button>
              <span className="text-xs text-muted-foreground tabular-nums">
                {page} / {totalPages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page >= totalPages || isLoading}
                onClick={() =>
                  void load(
                    page + 1,
                    appliedQuery,
                    selectedFollowUserids,
                    selectedWecomTagIds,
                    genderParam,
                    sort,
                    journeyFilter,
                  )
                }
              >
                下一页
              </Button>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
